/**
 * User Behavior Panel
 *
 * Renders signups, active users, and session breakdowns from
 * /api/admin/analytics/users.
 */
import { Loader2, UserPlus, Users, Activity } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAdminUsers, type AnalyticsRange } from "@/hooks/useAdminAnalytics";

interface Props {
  range: AnalyticsRange;
}

export function UserBehaviorPanel({ range }: Props) {
  const { data, isLoading, error } = useAdminUsers(range);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[#B87333]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-slate-600">
          Unable to load user analytics.
        </CardContent>
      </Card>
    );
  }

  const stats = [
    {
      label: "New Signups",
      value: data.new_signups,
      icon: <UserPlus className="h-5 w-5" />,
    },
    {
      label: "Total Users",
      value: data.total_users,
      icon: <Users className="h-5 w-5" />,
    },
    {
      label: "Active Users",
      value: data.active_users,
      icon: <Activity className="h-5 w-5" />,
    },
    {
      label: "Authed Sessions",
      value: data.authed_sessions,
      icon: <Activity className="h-5 w-5" />,
    },
  ];

  const totalSessions = data.authed_sessions + data.anon_sessions;
  const authedPct =
    totalSessions > 0
      ? Math.round((data.authed_sessions / totalSessions) * 100)
      : 0;

  const dailyData = data.daily_signups.map((d) => ({
    date: d.date.slice(5),
    signups: d.signups,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="border-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                {s.label}
              </CardTitle>
              <div className="p-2 bg-slate-100 rounded-lg text-slate-600">
                {s.icon}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                {s.value.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New Signups Over Time</CardTitle>
          <CardDescription>Daily user account creations.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="signups"
                  stroke="#B87333"
                  fill="#B87333"
                  fillOpacity={0.2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Session Breakdown</CardTitle>
          <CardDescription>
            Authenticated vs. anonymous sessions in the window.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {totalSessions === 0 ? (
            <p className="text-sm text-slate-500">
              No sessions recorded in this range.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                <span>Authenticated</span>
                <span>
                  {data.authed_sessions.toLocaleString()} ({authedPct}%)
                </span>
              </div>
              <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#B87333]"
                  style={{ width: `${authedPct}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                <span>Anonymous</span>
                <span>
                  {data.anon_sessions.toLocaleString()} ({100 - authedPct}%)
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default UserBehaviorPanel;
